import "./App.css";
import { SnackbarProvider } from "notistack";
import { ConfigProvider } from "antd";
import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

// Normal imports
import Home from "./pages/HomePage/Home";
import VendorStore from "./pages/VendorStore/VendorStore";
import ProductDescriptionPage from "./pages/ProductDescriptionPage/ProductDescriptionPage";
import ResultsPage from "./pages/ResultsPage/ResultsPage";
import UserDetails from "./pages/UserDetails/UserDetails";
import Loader from "./components/Loader/Loader";
import Login from "./pages/AuthPage/Login";
import Signup from "./pages/AuthPage/Signup";
import Subscription from "./pages/Subscription/Subscription";
import Plans from "./pages/Subscription/Plans";
import PaymentPage from "./components/Plans/PaymentPage";
import MyTeam from "./pages/MyTeam/MyTeam";
import Products from "./pages/Products/Products";
import Measure from "./pages/Measure/FaceMeasure";
import Layout from "./components/Layout/Layout";
import PublicReport from "./pages/PublicReport/PublicReport";
import FaceMeshMeasureRN from "./pages/Measure/FaceMeasureRN";
import ConsentForm from "./pages/ConsetForm/conset-form";
import VendorDashboard from "./pages/Vendor/VendorDashboard";
import NotFound from "./pages/NotFound/NotFound";
import VendorProfile from "./pages/VendorProfile/VendorProfile";

/*
 * Z-INDEX SCALE
 *   navbar (MUI AppBar)      1201   theme.zIndex.drawer + 1
 *   navbar drawer            1202
 *   antd overlays          >=1300   zIndexPopupBase, below
 *
 * antd's default zIndexPopupBase is 1000, which put every modal AND its
 * dimming mask *underneath* the 1201 navbar. Two consequences: the
 * translucent glass navbar showed the dimmed page through itself (reading as
 * a grey wash with the opaque logo punched through it), and nav links stayed
 * clickable while a modal was open. Raising the base here fixes it for every
 * antd overlay at once, including the static Modal.confirm / .success /
 * .error calls, which take no zIndex prop and so could not be fixed
 * per-instance.
 */
/*
 * Route guard.
 *
 * The private route group had none: every "private" page rendered its full
 * chrome to a signed-out visitor - navbar, page head, empty tables - and
 * only failed once its API calls came back rejected. You could read the
 * whole shape of the app without an account.
 *
 * This checks for a token, not a VALID one. Validity is the server's
 * answer, and the user store already clears a dead token when the API
 * rejects it. The guard's job is to stop the obvious case, not to
 * authenticate.
 *
 * `state.from` carries where they were headed, so signing in returns them
 * there instead of dumping everyone on /home.
 */
function RequireAuth() {
  const location = useLocation();
  const token = localStorage.getItem("userToken");

  if (!token) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

const antdTheme = {
  token: {
    zIndexPopupBase: 1300,

    /*
     * antd's default colorPrimary is #1677ff, and it was never overridden -
     * so every antd primary button in the app painted in antd's blue rather
     * than the brand accent, and eight separate stylesheets had grown a
     * hand-written `.ant-btn-primary { background-color: ... }` patch to
     * put it back. Setting it once here is the fix those patches were
     * approximating; they can go as each page is touched.
     *
     * Kept as literals rather than var(--accent): antd resolves these in JS
     * to generate its CSS-in-JS palette (hover, active, disabled steps are
     * derived from this value), and a var() reference is an opaque string
     * to that algorithm.
     */
    colorPrimary: "#2f45da",
    colorLink: "#2f45da",
    borderRadius: 10,
    fontFamily:
      '"Hanken Grotesk", system-ui, -apple-system, "Segoe UI", sans-serif',
  },
};

function App() {
  return (
    <ConfigProvider theme={antdTheme}>
    <SnackbarProvider maxSnack={3}>
      <Router>
        <Routes>
          {/* Full Screen Layout Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/user-auth" element={<Login />} />
          <Route path="/vendors" element={<VendorDashboard />} />
          {/* /vendor is a common typo for the real route. Redirect rather
              than mount a second copy, so there is one canonical URL. */}
          <Route path="/vendor" element={<Navigate to="/vendors" replace />} />
          <Route path="/vendor-dashboard" element={<VendorDashboard />} />
          {/* Full-screen, not inside .app-offset-top: that wrapper reserves
              72px for the fixed navbar, which this page does not render, so
              inside it the page opened with a band of dead space. It brings
              its own sticky header instead. */}
          <Route path="/vendor/:slug" element={<VendorProfile />} />

          {/* Standard wrapper layout routes */}
          <Route
            path="/*"
            element={
              <div className="app-offset-top">
                <div className="app-page">
                  <Routes>
                    {/* Genuinely public, and it has to stay that way: the
                        product page generates this URL to be SHARED, and the
                        page reads from the /open endpoint. Behind the guard,
                        every link already sent out would break. */}
                    <Route path="/public-report/:id" element={<PublicReport />} />

                    {/* The vendor directory, public.

                        It was behind the guard, which was inconsistent with
                        the two things it is made of: it reads from /open, an
                        unauthenticated endpoint, and every card links to
                        /vendor/:slug, which is public. Only the index was
                        gated, so a visitor could open any single listing but
                        not the list of them.

                        It is also what the marketing site now points at, and
                        a directory that demands an account before showing who
                        is in it cannot do that job. Inside Layout so it still
                        carries the navbar, which renders its signed-out
                        state on its own. */}
                    <Route element={<Layout />}>
                      <Route path="/vendor-store" element={<VendorStore />} />
                    </Route>

                    {/* Everything below needs an account. */}
                    <Route element={<RequireAuth />}>
                      <Route path="/user-details" element={<UserDetails />} />
                      <Route path="/test" element={<Loader />} />
                      <Route path="/subscription" element={<Subscription />} />
                      <Route path="/payment" element={<PaymentPage />} />
                      <Route path="/rn-measure" element={<FaceMeshMeasureRN />} />
                      {/* A step inside the signed-in journey, not a patient
                          link: it writes consent to localStorage and returns
                          to whatever set redirectAfterConsent. */}
                      <Route path="/consent-form" element={<ConsentForm />} />

                      {/* Private Routes with Navbar */}
                      <Route element={<Layout />}>
                      <Route path="/home" element={<Home />} />
                      <Route
                        path="/product-description/:id"
                        element={<ProductDescriptionPage />}
                      />
                      <Route path="/product-results" element={<ResultsPage />} />
                      <Route path="/myteam" element={<MyTeam />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/measure" element={<Measure />} />
                      <Route path="/plans" element={<Plans />} />
                      {/* /bulk-upload route dropped: bulk upload is now a
                          modal opened from the Products toolbar, and the
                          component exports a controlled modal rather than a
                          page, so a bare route would render nothing. */}

                      {/* Anything unmatched lands here instead of an empty
                          render. Inside Layout so the navbar stays and the
                          user is not stranded. */}
                        <Route path="*" element={<NotFound />} />
                      </Route>
                    </Route>
                  </Routes>
                </div>
              </div>
            }
          />
        </Routes>
      </Router>
    </SnackbarProvider>
    </ConfigProvider>
  );
}

export default App;
